import { useState, useEffect, FormEvent, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { aboutData } from '@/mocks/about';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const formUrl = 'https://readdy.ai/api/form/d9u0nol3pcjqs2fcsk3g';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formUrl) {
      setFormStatus('error');
      setFormError('表单暂时不可用，请通过其他方式联系我们');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const form = e.currentTarget;
      const formData = new FormData(form);

      // Check honeypot
      const honeypotVal = formData.get('phone_alt');
      if (honeypotVal && typeof honeypotVal === 'string' && honeypotVal.trim() !== '') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
        return;
      }

      formData.delete('phone_alt');

      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormError(msg.includes('spam') ? '提交失败，请稍后重试' : msg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  }, [formUrl]);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': '关于我们 - 银河娱乐官方平台',
    'description': '了解银河娱乐的品牌故事、发展历程、权威认证与安全保障体系。银河娱乐成立于2010年，服务超过500万会员，是行业领先的在线娱乐品牌。',
    'url': 'https://51xlzxs.cn/about',
    'breadcrumb': {
      '@type': 'BreadcrumbList',
      'itemListElement': [
        { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://51xlzxs.cn/' },
        { '@type': 'ListItem', 'position': 2, 'name': '关于我们', 'item': 'https://51xlzxs.cn/about' },
      ],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': aboutData.faqs.map((faq) => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 银河娱乐官方平台"
        description="了解银河娱乐的品牌故事、发展历程、权威认证与安全保障体系。银河娱乐成立于2010年，服务超过500万会员，是行业领先的在线娱乐品牌。"
        keywords="银河娱乐介绍,银河娱乐品牌,银河娱乐信誉,银河娱乐联系方式,银河娱乐客服"
        canonicalUrl="https://51xlzxs.cn/about"
        schemaData={{ ...schemaData, ...faqSchema }}
      />

      <Navbar />

      <main className="pt-20">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto pt-8 pb-4">
            <Breadcrumb />
            <div className="px-4 md:px-6 pb-8">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">关于我们</h1>
              <p className="text-foreground-600 mt-3 text-base">了解银河娱乐的品牌故事与发展历程</p>
            </div>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block px-4 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold tracking-wider uppercase mb-4">品牌故事</span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">{aboutData.brandIntro.title}</h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  {aboutData.brandIntro.paragraphs.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Luxurious%20modern%20corporate%20headquarters%20building%20with%20golden%20glass%20facade%20at%20sunset%2C%20professional%20office%20interior%20visible%20through%20windows%2C%20warm%20golden%20hour%20lighting%2C%20premium%20corporate%20photography%2C%20elegant%20architecture%2C%20business%20success%20symbol&width=800&height=600&seq=about-brand-01&orientation=landscape"
                  alt="银河娱乐品牌总部"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <span className="inline-block px-4 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold tracking-wider uppercase mb-4">发展历程</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">品牌里程碑</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {aboutData.milestones.map((m) => (
                <div key={m.year} className="text-center p-5 rounded-xl bg-background-50 cursor-default">
                  <span className="inline-block font-heading text-2xl font-bold text-primary-500 mb-2">{m.year}</span>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">{m.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{m.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Certifications */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <span className="inline-block px-4 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold tracking-wider uppercase mb-4">信誉保障</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">权威认证与资质</h2>
              <p className="text-foreground-600 max-w-2xl mx-auto text-base">
                银河娱乐持有多项国际权威认证，始终以最高标准要求自己，确保为每一位玩家提供安全可靠的娱乐环境
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {aboutData.certifications.map((cert) => (
                <div key={cert.name} className="text-center p-6 rounded-xl bg-background-100 cursor-default">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 mx-auto mb-4">
                    <i className={`${cert.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">{cert.name}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{cert.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold tracking-wider uppercase mb-4">常见问题</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">您可能想了解</h2>
            </div>
            <div className="space-y-3">
              {aboutData.faqs.map((faq, index) => (
                <div key={index} className="rounded-xl bg-background-50 overflow-hidden">
                  <button
                    onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer transition-colors hover:bg-background-100"
                  >
                    <span className="font-heading text-base font-semibold text-foreground-950 pr-4">{faq.question}</span>
                    <i className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform duration-300 flex-shrink-0 w-5 h-5 flex items-center justify-center ${activeFaq === index ? 'rotate-180' : ''}`}></i>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      activeFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <p className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <span className="inline-block px-4 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold tracking-wider uppercase mb-4">联系我们</span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">随时为您服务</h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                  银河娱乐客服团队7×24小时全天候在线，随时为您解答疑问、处理问题。您可以通过以下任意方式联系我们：
                </p>
                <div className="space-y-5">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-phone-line text-xl"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-950">客服热线</div>
                      <div className="text-sm text-foreground-600">{aboutData.contactInfo.phone}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line text-xl"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-950">电子邮箱</div>
                      <div className="text-sm text-foreground-600">{aboutData.contactInfo.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-wechat-line text-xl"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-950">官方微信</div>
                      <div className="text-sm text-foreground-600">{aboutData.contactInfo.wechat}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-time-line text-xl"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-950">服务时间</div>
                      <div className="text-sm text-foreground-600">{aboutData.contactInfo.workingHours}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="rounded-xl bg-background-100 p-6 md:p-8">
                <h3 className="font-heading text-xl font-bold text-foreground-950 mb-6">发送消息</h3>
                <form ref={formRef} onSubmit={handleSubmit} data-readdy-form className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200/60 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200/60 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200/60 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入咨询主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200/60 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      placeholder="请输入您的留言（最多500字）"
                    ></textarea>
                  </div>
                  {/* Honeypot */}
                  <div className="sp-protect">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'loading' || !formUrl}
                    className="w-full py-3.5 text-sm font-bold rounded-lg bg-primary-500 text-background-50 cursor-pointer whitespace-nowrap transition-all duration-300 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'loading' ? '发送中...' : '发送消息'}
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-primary-600 font-medium text-center">消息已发送，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && formError && (
                    <p className="text-sm text-accent-600 font-medium text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16 md:py-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Golden%20luxurious%20abstract%20background%20with%20flowing%20silk%20texture%2C%20warm%20golden%20and%20amber%20gradient%2C%20soft%20bokeh%20particles%2C%20elegant%20premium%20wallpaper%2C%20dark%20edges%20with%20bright%20center%20glow%2C%20cinematic%20composition&width=1920&height=600&seq=about-cta-01&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-background-900/75"></div>
          </div>
          <div className="relative z-10 text-center px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">准备好开始您的娱乐之旅了吗？</h2>
            <p className="text-background-50/70 mb-8 max-w-lg mx-auto text-sm">立即注册成为银河娱乐会员，尊享新人专属礼包与豪华会员权益</p>
            <Link
              to="/"
              className="inline-flex px-10 py-3.5 text-sm font-bold rounded-full bg-primary-500 text-background-50 cursor-pointer whitespace-nowrap transition-all duration-300 hover:bg-primary-600 hover:scale-105"
            >
              立即注册
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}